import { type SolarElectricalChange } from './solarElectricalChange';
import { SOLAR_DC_AC_RATIO_WINDOW } from './solarInverter';
import { solarSerpentinePanelOrder } from './solarAutoStringPath';
import { type LatLng } from './solarFacePacker';
import {
  type SolarElectricalTopology,
  type SolarIdentifiedPanel,
} from './solarElectricalTopology';

export type SolarAutoStringInput = {
  topology: SolarElectricalTopology;
  /**
   * With corners on every panel of a roof, strings follow a serpentine path
   * through it; otherwise they follow the roof's panels in list order.
   */
  panels: (SolarIdentifiedPanel & { corners?: readonly LatLng[] })[];
  faces: { id: number; azimuthDegrees: number }[];
  excludedFaceIds: number[];
  /** Defaults 8 and 13. */
  rules?: { minPanelsPerString?: number; maxPanelsPerString?: number };
  /** New string identities; defaults to `crypto.randomUUID()`. Inject for deterministic tests. */
  ids?: () => string;
  /**
   * Strings a unit may parallel on one MPPT (see solarMaxStringsPerMppt).
   * Default one: strings are never paralleled unless the catalogue allows it.
   */
  maxStringsPerMppt?: (
    instance: SolarElectricalTopology['inverterInstances'][number]
  ) => number | null | undefined;
};

export type SolarAutoStringResult = {
  /** Apply in order through `applySolarElectricalDesignChange`. */
  changes: SolarElectricalChange[];
  summary: {
    strings: number;
    assignedPanels: number;
    unassignedPanels: number;
    notes: string[];
  };
};

type Roof = {
  faceId: number;
  label: string;
  /** Eligible panel ids in the order they are strung. */
  panelIds: string[];
  /** Strings the roof needs at the maximum length. */
  demand: number;
};

type Mppt = {
  unitIndex: number;
  key: string;
  inputKeys: string[];
  /** Strings it may hold in parallel: min(inputs, the unit's per-MPPT allowance). */
  capacity: number;
  faceId: number | null;
  /** Strings connected one per input, in input order. */
  connected: string[][];
};

type Unit = { index: number; letter: string; load: number; mppts: Mppt[] };

const DEFAULT_MIN = 8;
const DEFAULT_MAX = 13;

/** A, B, … Z, AA, AB … following inverterInstances order. */
const unitLetter = (index: number): string => {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

const letterOf = (name: string) =>
  /^Inverter ([A-Z]+)$/.exec(name.trim())?.[1] ?? null;

/**
 * String-name letters per unit: the letter in "Inverter C"; units named
 * otherwise take the first letter no unit name claims, in unit order.
 */
const unitLetters = (names: readonly string[]): string[] => {
  const claimed = new Set(names.flatMap((name) => letterOf(name) ?? []));
  let next = 0;
  return names.map((name) => {
    const own = letterOf(name);
    if (own) return own;
    while (claimed.has(unitLetter(next))) next += 1;
    const letter = unitLetter(next);
    claimed.add(letter);
    return letter;
  });
};

type Layout = { strings: number[]; lengths: number[]; used: number };

/**
 * Strings per MPPT (each ≤ its capacity) and one length per MPPT (≤ max) that
 * connect the most of `pool` panels. Starts at one string per MPPT and only
 * parallels a second string when that connects more panels; ties keep fewer
 * strings. Parallel strings on one MPPT share its length.
 */
const layoutRoof = (
  capacities: number[],
  pool: number,
  max: number
): Layout => {
  const fill = (strings: number[]): Layout => {
    const total = strings.reduce((sum, value) => sum + value, 0);
    const base = Math.min(max, Math.floor(pool / total));
    const lengths = strings.map(() => base);
    let left = pool - base * total;
    // Lengthen MPPTs one panel per string at a time, single strings first.
    const order = strings
      .map((value, index) => ({ value, index }))
      .sort((a, b) => a.value - b.value || a.index - b.index);
    for (let grew = true; grew; ) {
      grew = false;
      for (const { value, index } of order)
        if (lengths[index] < max && left >= value) {
          lengths[index] += 1;
          left -= value;
          grew = true;
        }
    }
    return { strings, lengths, used: pool - left };
  };
  let best = fill(capacities.map(() => 1));
  for (;;) {
    let next: Layout | null = null;
    capacities.forEach((capacity, index) => {
      if (best.strings[index] >= capacity) return;
      const trial = fill(
        best.strings.map((value, i) => (i === index ? value + 1 : value))
      );
      if (trial.used > (next?.used ?? best.used)) next = trial;
    });
    if (!next) return best;
    best = next;
  }
};

/**
 * Aurora-style auto-stringing: every roof gets its own MPPT before any roof
 * gets a second, spare MPPTs go to the roofs needing more strings, and only
 * then are strings paralleled, on units whose catalogue allows it
 * (`maxStringsPerMppt`, default one). Strings on one MPPT are always the same
 * length; a roof re-splits over the MPPTs it holds (up to the maximum length)
 * before panels are left off. Roofs are spread across units to balance DC.
 * Pure; returns reducer changes only.
 */
export const autoStringSolarTopology = (
  input: SolarAutoStringInput
): SolarAutoStringResult => {
  const { topology, panels, faces, excludedFaceIds } = input;
  const nextId = input.ids ?? (() => crypto.randomUUID());
  const max = Math.max(
    1,
    Math.floor(input.rules?.maxPanelsPerString ?? DEFAULT_MAX)
  );
  const min = Math.min(
    max,
    Math.max(1, Math.floor(input.rules?.minPanelsPerString ?? DEFAULT_MIN))
  );
  const notes: string[] = [];

  const excluded = new Set(excludedFaceIds);
  const included = panels.filter(
    (panel) => panel.included && !excluded.has(panel.faceId)
  );
  const eligible = included.filter(
    (panel): panel is SolarAutoStringInput['panels'][number] & { id: string } =>
      !!panel.id
  );
  const eligibleCount = new Set(eligible.map((panel) => panel.id)).size;
  const missingIds = included.length - eligible.length;

  // Roofs in face order (as labelled in the designer), then any unlisted faces by first appearance.
  const faceOrder = faces.map((face) => face.id);
  const roofLabel = (faceId: number) => {
    const index = faceOrder.indexOf(faceId);
    return `Roof ${index >= 0 ? index + 1 : faceId}`;
  };
  const byFace = new Map<number, typeof eligible>();
  const seen = new Set<string>();
  for (const panel of eligible) {
    if (seen.has(panel.id)) continue;
    seen.add(panel.id);
    const list = byFace.get(panel.faceId) ?? [];
    list.push(panel);
    byFace.set(panel.faceId, list);
  }
  const strungOrder = (list: typeof eligible) =>
    solarSerpentinePanelOrder(
      list.map((panel) => ({ id: panel.id, corners: panel.corners ?? [] }))
    ) ?? list.map((panel) => panel.id);
  const roofRank = (faceId: number) => {
    const index = faceOrder.indexOf(faceId);
    return index >= 0 ? index : faceOrder.length;
  };
  const roofs: Roof[] = [...byFace.entries()]
    .sort(([a], [b]) => roofRank(a) - roofRank(b))
    .map(([faceId, list]) => ({
      faceId,
      label: roofLabel(faceId),
      panelIds: strungOrder(list),
      demand: Math.max(1, Math.ceil(list.length / max)),
    }));

  const units: Unit[] = [];
  const noInputs: string[] = [];
  const letters = unitLetters(
    topology.inverterInstances.map((instance) => instance.name)
  );
  topology.inverterInstances.forEach((instance, index) => {
    const allowed = input.maxStringsPerMppt?.(instance);
    const perMppt =
      allowed != null && Number.isFinite(allowed) && allowed >= 1
        ? Math.floor(allowed)
        : 1;
    const mppts: Mppt[] = instance.trackers
      .filter((tracker) => tracker.inputs.length > 0)
      .map((tracker) => ({
        unitIndex: index,
        key: tracker.key,
        inputKeys: tracker.inputs.map((item) => item.key),
        capacity: Math.min(tracker.inputs.length, perMppt),
        faceId: null,
        connected: [],
      }));
    if (!mppts.length) {
      noInputs.push(instance.name);
      return;
    }
    units.push({
      index,
      letter: letters[index],
      load: 0,
      mppts,
    });
  });

  if (noInputs.length)
    notes.push(
      noInputs.length === 1
        ? `${noInputs[0]} has no inputs`
        : `${noInputs.slice(0, -1).join(', ')} and ${noInputs.at(-1)} have no inputs`
    );
  if (missingIds > 0)
    notes.push(
      `${missingIds} panel${missingIds === 1 ? ' has' : 's have'} no ID and ${missingIds === 1 ? 'was' : 'were'} not strung`
    );

  if (!units.length) {
    if (eligibleCount > 0)
      notes.unshift(
        `${eligibleCount} panel${eligibleCount === 1 ? ' has' : 's have'} no inverter input`
      );
    return {
      changes: [],
      summary: {
        strings: 0,
        assignedPanels: 0,
        unassignedPanels: eligibleCount,
        notes,
      },
    };
  }

  // Demand first: the roof needing the most strings claims MPPTs first; ties
  // go to the larger roof, then roof order.
  const queue = [...roofs].sort(
    (a, b) => b.demand - a.demand || b.panelIds.length - a.panelIds.length
  );
  const held = (roof: Roof) =>
    units
      .flatMap((unit) => unit.mppts)
      .filter((mppt) => mppt.faceId === roof.faceId);
  const freeInputs = (unit: Unit) =>
    unit.mppts
      .filter((mppt) => mppt.faceId == null)
      .reduce((sum, mppt) => sum + mppt.capacity, 0);

  /**
   * The smallest free MPPT that could hold every wanted string; when none
   * can, the free MPPT with the most capacity on the unit with the most free
   * capacity. Ties go to the unit with the least DC so far, then inverter order.
   */
  const freeMppt = (wanted: number): Mppt | null => {
    const free = units.flatMap((unit) =>
      unit.mppts
        .filter((mppt) => mppt.faceId == null)
        .map((mppt) => ({ unit, mppt }))
    );
    const balance = (a: { unit: Unit }, b: { unit: Unit }) =>
      a.unit.load - b.unit.load || a.unit.index - b.unit.index;
    const fitting = free
      .filter(({ mppt }) => mppt.capacity >= wanted)
      .sort((a, b) => a.mppt.capacity - b.mppt.capacity || balance(a, b));
    if (fitting[0]) return fitting[0].mppt;
    return (
      [...free].sort(
        (a, b) =>
          b.mppt.capacity - a.mppt.capacity ||
          freeInputs(b.unit) - freeInputs(a.unit) ||
          balance(a, b)
      )[0]?.mppt ?? null
    );
  };
  const claim = (roof: Roof, mppt: Mppt) => {
    mppt.faceId = roof.faceId;
    const unit = units.find((item) => item.index === mppt.unitIndex);
    // Estimated DC until the roof is laid out.
    if (unit) unit.load += Math.ceil(roof.panelIds.length / roof.demand);
  };
  const unmet = (roof: Roof) => roof.demand - held(roof).length;

  // Pass 1: every roof, in demand order, gets one MPPT while MPPTs last.
  for (const roof of queue) {
    const mppt = freeMppt(roof.demand);
    if (!mppt) break;
    claim(roof, mppt);
  }
  // Pass 2: spare MPPTs go to the roof with the most strings still unplaced,
  // so a roof spans several MPPTs before any string is paralleled.
  for (;;) {
    const roof = queue
      .filter((item) => unmet(item) > 0)
      .sort((a, b) => unmet(b) - unmet(a))[0];
    if (!roof) break;
    const mppt = freeMppt(unmet(roof));
    if (!mppt) break;
    claim(roof, mppt);
  }
  // Lay each roof out over its MPPTs in unit order, longest strings first.
  const leftOff = new Map<number, { panels: number; paralleled: boolean }>();
  const mpptOrder = units.flatMap((unit) => unit.mppts);
  for (const unit of units) unit.load = 0;
  for (const roof of queue) {
    const mine = mpptOrder.filter((mppt) => mppt.faceId === roof.faceId);
    if (!mine.length) continue;
    const layout = layoutRoof(
      mine.map((mppt) => mppt.capacity),
      roof.panelIds.length,
      max
    );
    let offset = 0;
    // Slices follow MPPT order; layoutRoof lengthens earlier MPPTs first.
    mine.forEach((mppt, index) => {
      const strings = layout.strings[index];
      const length = layout.lengths[index];
      mppt.connected = Array.from({ length: strings }, () => {
        const chunk = roof.panelIds.slice(offset, offset + length);
        offset += length;
        return chunk;
      }).filter((chunk) => chunk.length > 0);
      const unit = units.find((item) => item.index === mppt.unitIndex);
      if (unit) unit.load += strings * length;
    });
    const left = roof.panelIds.length - layout.used;
    if (left > 0)
      leftOff.set(roof.faceId, {
        panels: left,
        paralleled: layout.strings.some((value) => value > 1),
      });
  }

  // A roof left short only because parallel strings must match gets its own
  // note; otherwise the worst-served roof explains the shortfall in one line.
  const equalised = (roof: Roof) => {
    const left = leftOff.get(roof.faceId);
    return left?.paralleled && left.panels < min ? left.panels : null;
  };
  const connectedStrings = (roof: Roof) =>
    held(roof).reduce((sum, mppt) => sum + mppt.connected.length, 0);
  const short = [...queue]
    .filter(
      (roof) =>
        equalised(roof) == null &&
        held(roof).reduce(
          (sum, mppt) => sum + mppt.connected.flat().length,
          0
        ) < roof.panelIds.length
    )
    .map((roof) => ({
      roof,
      connected: connectedStrings(roof),
      waiting: roof.demand - connectedStrings(roof),
    }))
    .sort((a, b) => b.waiting - a.waiting)[0];

  // Strings with no free input are not created: their panels stay unassigned.
  const changes: SolarElectricalChange[] = topology.strings.map((string) => ({
    type: 'removeString',
    id: string.id,
  }));
  let strings = 0;
  let assignedPanels = 0;
  let shortStrings = 0;
  const instances = topology.inverterInstances;
  for (const unit of units) {
    let n = 0;
    const instanceId = instances[unit.index].id;
    for (const mppt of unit.mppts) {
      mppt.connected.forEach((chunk, index) => {
        n += 1;
        const id = nextId();
        changes.push({ type: 'addString', id, name: `${unit.letter}${n}` });
        changes.push({ type: 'assignPanels', stringId: id, panelIds: chunk });
        changes.push({
          type: 'connectString',
          id,
          inverterInstanceId: instanceId,
          mpptKey: mppt.key,
          inputKey: mppt.inputKeys[index],
        });
        strings += 1;
        assignedPanels += chunk.length;
        if (chunk.length < min) shortStrings += 1;
      });
    }
  }
  const unassignedPanels = eligibleCount - assignedPanels;
  if (unassignedPanels > 0)
    notes.unshift(
      `${unassignedPanels} panel${unassignedPanels === 1 ? ' has' : 's have'} no inverter input`
    );
  if (short)
    notes.unshift(
      `${short.roof.label} needs ${short.roof.demand} string${short.roof.demand === 1 ? '' : 's'} · ${short.connected} connected`
    );
  for (const roof of queue) {
    const left = equalised(roof) ?? 0;
    if (left > 0)
      notes.unshift(
        `${roof.label}: ${left} panel${left === 1 ? '' : 's'} left off so parallel strings match`
      );
  }
  if (shortStrings > 0)
    notes.push(
      `${shortStrings} string${shortStrings === 1 ? '' : 's'} below the ${min}-panel minimum`
    );

  return {
    changes,
    summary: {
      strings,
      assignedPanels,
      unassignedPanels,
      notes,
    },
  };
};

export type SolarInverterFleetProduct = {
  applianceId: string;
  name: string;
  acOutputKw: number | null;
  mpptCount: number | null;
  phases?: number | null;
};

export type SolarInverterFleetOption = {
  productId: string;
  name: string;
  /** Units of this product the whole design needs alongside other products already in use. */
  unitsNeeded: number;
  /** unitsNeeded minus the units of this product already in the design. */
  unitsToAdd: number;
  /** Design DC over all AC (existing other units plus unitsNeeded of this product). */
  ratio: number;
  /** The ratio is outside the 0.8–1.3 window; the note says so. */
  outsideWindow: boolean;
  note: string;
};

export type SolarInverterFleetSuggestion = SolarInverterFleetOption & {
  /** A product needing fewer units than the suggestion, when the account has one. */
  alternative: SolarInverterFleetOption | null;
};

const inWindow = (value: number) =>
  value >= SOLAR_DC_AC_RATIO_WINDOW.min &&
  value <= SOLAR_DC_AC_RATIO_WINDOW.max;

const windowNote = (ratio: number) =>
  inWindow(ratio)
    ? ''
    : ` · outside ${SOLAR_DC_AC_RATIO_WINDOW.min.toFixed(2)}–${SOLAR_DC_AC_RATIO_WINDOW.max.toFixed(2)}`;

/**
 * Sizes the inverter fleet before stringing: for each product with a known AC
 * rating, the unit count whose DC/AC is nearest the target (1.15) inside the
 * shared 0.8–1.3 window (rounded, then n − 1 and n + 1 checked), keeping
 * units of other products already in the design. Picks the product in use
 * when it lands within the window, else the one needing the fewest units.
 * Products on the wrong supply are left out when the supply is known, units
 * already covering the array suggest none more, and a pick outside the window
 * says so. Returns null when no product has an AC rating.
 */
export const suggestSolarInverterFleet = ({
  totalDcKw,
  products,
  existingUnits,
  targetRatio = SOLAR_DC_AC_RATIO_WINDOW.target,
  supplyPhases = null,
}: {
  totalDcKw: number;
  products: SolarInverterFleetProduct[];
  existingUnits: { applianceId: string | null }[];
  targetRatio?: number;
  /**
   * The site supply when known: single-phase leaves out three-phase
   * products; three-phase prefers three-phase products when there are any.
   */
  supplyPhases?: 1 | 3 | null;
}): SolarInverterFleetSuggestion | null => {
  if (!(totalDcKw > 0)) return null;
  const allRated = products.filter(
    (product): product is SolarInverterFleetProduct & { acOutputKw: number } =>
      product.acOutputKw != null && product.acOutputKw > 0
  );
  if (!allRated.length) return null;
  const acOf = (applianceId: string | null) =>
    allRated.find((product) => product.applianceId === applianceId)
      ?.acOutputKw ?? 0;
  // Units already in the design that cover the array: none more.
  const existingAc = existingUnits.reduce(
    (sum, unit) => sum + acOf(unit.applianceId),
    0
  );
  if (
    existingAc > 0 &&
    totalDcKw / existingAc <= SOLAR_DC_AC_RATIO_WINDOW.max
  ) {
    const ratio = totalDcKw / existingAc;
    const first = allRated.find((product) =>
      existingUnits.some((unit) => unit.applianceId === product.applianceId)
    )!;
    const own = existingUnits.filter(
      (unit) => unit.applianceId === first.applianceId
    ).length;
    return {
      productId: first.applianceId,
      name: first.name,
      unitsNeeded: own,
      unitsToAdd: 0,
      ratio,
      outsideWindow: !inWindow(ratio),
      note: `Units in the design cover it (DC/AC ${ratio.toFixed(2)})${windowNote(ratio)}`,
      alternative: null,
    };
  }
  const onSupply = allRated.filter((product) =>
    supplyPhases === 1 ? product.phases !== 3 : true
  );
  const threePhase = onSupply.filter((product) => product.phases === 3);
  let rated = onSupply.length ? onSupply : allRated;
  if (supplyPhases === 3 && threePhase.length) rated = threePhase;
  const size =
    (keepOthers: boolean) =>
    (
      product: (typeof rated)[number]
    ): SolarInverterFleetOption & {
      fits: boolean;
      inUse: boolean;
    } => {
      const own = existingUnits.filter(
        (unit) => unit.applianceId === product.applianceId
      ).length;
      const otherAc = keepOthers
        ? existingUnits
            .filter((unit) => unit.applianceId !== product.applianceId)
            .reduce((sum, unit) => sum + acOf(unit.applianceId), 0)
        : 0;
      const ratioOf = (units: number) =>
        totalDcKw / (otherAc + units * product.acOutputKw);
      const nearest = Math.max(
        1,
        Math.round((totalDcKw / targetRatio - otherAc) / product.acOutputKw)
      );
      // Nearest the target, preferring counts inside the window, then fewer units.
      const unitsNeeded = [nearest - 1, nearest, nearest + 1]
        .filter((units) => units >= 1)
        .sort(
          (a, b) =>
            Number(inWindow(ratioOf(b))) - Number(inWindow(ratioOf(a))) ||
            Math.abs(ratioOf(a) - targetRatio) -
              Math.abs(ratioOf(b) - targetRatio) ||
            a - b
        )[0];
      const ratio = ratioOf(unitsNeeded);
      const phase = product.phases === 3 ? ' (three-phase)' : '';
      return {
        productId: product.applianceId,
        name: product.name,
        unitsNeeded,
        unitsToAdd: Math.max(0, unitsNeeded - own),
        ratio,
        outsideWindow: !inWindow(ratio),
        note: `${unitsNeeded} × ${product.name}${phase} (DC/AC ${ratio.toFixed(2)})${windowNote(ratio)}`,
        fits: inWindow(ratio),
        inUse: own > 0,
      };
    };
  const options = rated.map(size(true));
  // Alternatives replace the fleet rather than topping it up.
  const fresh = rated.map(size(false));
  const strip = ({
    fits: _fits,
    inUse: _inUse,
    ...option
  }: ReturnType<ReturnType<typeof size>>): SolarInverterFleetOption => option;
  const byUnits = [...options].sort(
    (a, b) =>
      Number(b.fits) - Number(a.fits) ||
      a.unitsNeeded - b.unitsNeeded ||
      Math.abs(a.ratio - targetRatio) - Math.abs(b.ratio - targetRatio)
  );
  const pick =
    options
      .filter((option) => option.inUse && option.fits)
      .sort((a, b) => a.unitsToAdd - b.unitsToAdd)[0] ?? byUnits[0];
  const fewer = fresh
    .filter(
      (option) =>
        option.productId !== pick.productId &&
        option.fits &&
        option.unitsNeeded < pick.unitsNeeded
    )
    .sort((a, b) => a.unitsNeeded - b.unitsNeeded)[0];
  return { ...strip(pick), alternative: fewer ? strip(fewer) : null };
};
